import { useEffect, useState, useCallback, useRef } from "react";
import API from "../../services/api";
import {
  Row,
  Col,
  Spin,
  Button,
  Empty,
  Space,
  Pagination,
  Modal,
  AutoComplete,
} from "antd";
import {
  FilterOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import "./CommercialSpaces.css";
import "./ValidationAlert.css";
import { filterCities, getLatLngForFilter } from "../locationService";
import {
  validateLocality,
  validateNumericRange,
  validatePositiveInteger,
  validateCustomText,
  clampNonNegative,
  isEmpty,
} from "../../utils/Filtervalidation.js";
import { useFilterValidation } from "../../hooks/Usefiltervalidation.js";
// ─────────────────────────────────────────────────────────────────────────────
// NEW: reusable location hook
// ─────────────────────────────────────────────────────────────────────────────
import { useLocationSearch } from "../../hooks/Uselocationsearch.js";

// ─────────────────────────────────────────────────────────────────────────────
// ALLOWED VALUES — prevents injection of arbitrary strings into API params
// ─────────────────────────────────────────────────────────────────────────────
const ALLOWED_TYPES = ["rent", "lease", "sell", "sale"];
const ALLOWED_SORT_BY = ["price_low", "price_high", "area_high"];
const ALLOWED_AMENITIES = [
  "Parking",
  "Lift",
  "AC",
  "Wi-Fi",
  "Power Backup",
  "Security",
  "CCTV",
];
const MAX_PRICE = 1_000_000_000;
const MAX_AREA = 10_000_000;
const MAX_AGE = 200;
const MAX_FLOOR = 200;

// ─────────────────────────────────────────────────────────────────────────────
// ValidationTooltip — native-browser-style tooltip shown below each field
// ─────────────────────────────────────────────────────────────────────────────
function ValidationTooltip({ message }) {
  if (!message) return null;
  return (
    <div className="vt-tooltip" role="alert" aria-live="polite">
      <span className="vt-tooltip-icon" aria-hidden="true" />
      <span className="vt-tooltip-text">{message}</span>
    </div>
  );
}

function CommercialSpaces() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);

  // ── Search-bar suggestion state (driven by useLocationSearch hook)
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  // ── Filter-modal locality suggestion state (separate, unchanged)
  const [filterSuggestions, setFilterSuggestions] = useState([]);

  const [openDropdown, setOpenDropdown] =
    useState(null);

  const [customPropertyTypeFilter, setCustomPropertyTypeFilter] = useState("");

  // ── Validation hook ────────────────────────────────────────────────────────
  const { fieldErrors, setFieldErrors, clearAllFieldErrors, scrollToFirstError } =
    useFilterValidation();

  // ── Refs for scroll-to-error ───────────────────────────────────────────────
  const localityRef = useRef(null);
  const priceRef = useRef(null);
  const areaRef = useRef(null);
  const maxAgeRef = useRef(null);
  const floorRef = useRef(null);
  const customPropertyTypeRef = useRef(null);
  const filterContentRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);

  // Responsive modal width
  const [modalWidth, setModalWidth] = useState(600);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 576) {
        setModalWidth("96vw");
      } else if (window.innerWidth <= 768) {
        setModalWidth("95vw");
      } else {
        setModalWidth(600);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    locality: "",
    latitude: null,
    longitude: null,
    type: "",
    propertyType: "",
    minPrice: "",
    maxPrice: "",
    minArea: "",
    maxArea: "",
    furnished: null,
    amenities: [],
    minAge: "",
    maxAge: "",
    floor: "",
    negotiable: null,
    sortBy: "",
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  // ── NEW: reusable location-search hook for the main search bar ─────────────
  const {
    inputValue: searchInputValue,
    isLocationValid: isSearchLocationValid,
    handleChange: handleSearchChange,
    handleSelect: handleSearchSelect,
    handleKeyDown: makeSearchKeyDown,
    handleFocus: handleSearchFocus,
    resetLocation: resetSearchLocation,
  } = useLocationSearch({
    onSuggestionsChange: setSearchSuggestions,
    debounceMs: 300,
    minChars: 2,

    // SYNC FILTER → SEARCH
    syncedLocality: filters.locality,

    // SYNC SEARCH → FILTER
    onSyncLocality: (value) => {

      setFilters((prev) => {

        if (prev.locality === value) {
          return prev;
        }

        return {
          ...prev,
          locality: value,
        };
      });
    },
  });

  // filterDebounceRef is still used by the filter-modal locality field
  const filterDebounceRef = useRef(null);

  // ── Sanitize a numeric filter value before storing in state ──────────────
  const sanitizeNumericInput = (value, max) => {
    if (value === "" || value === null || value === undefined) {
      return "";
    }

    // ONLY positive integers allowed
    const stripped = String(value).replace(/[^0-9]/g, "");

    if (stripped === "") {
      return "";
    }

    const n = Number(stripped);

    if (!Number.isInteger(n) || n < 0) {
      return "";
    }

    if (max !== undefined && n > max) {
      return String(max);
    }

    return stripped;
  };

  // ── Build clean params object from current filters ───────────────────────
  const buildParams = useCallback((customFilters, page, pageSize) => {
    const params = { page, limit: pageSize };

    if (customFilters.search?.trim()) {
      params.search = customFilters.search.trim().slice(0, 200);
    }
    if (customFilters.locality?.trim()) {
      params.locality = customFilters.locality.trim().slice(0, 100);
    }

    if (customFilters.type && ALLOWED_TYPES.includes(customFilters.type)) {
      params.type = customFilters.type;
    }
    if (customFilters.propertyType) {
      params.propertyType = String(customFilters.propertyType).slice(0, 100);
    }

    const minPrice = Number(customFilters.minPrice);
    const maxPrice = Number(customFilters.maxPrice);
    if (customFilters.minPrice !== "" && isFinite(minPrice) && minPrice >= 0) {
      params.minPrice = minPrice;
    }
    if (customFilters.maxPrice !== "" && isFinite(maxPrice) && maxPrice >= 0) {
      params.maxPrice = maxPrice;
    }

    const minArea = Number(customFilters.minArea);
    const maxArea = Number(customFilters.maxArea);
    if (customFilters.minArea !== "" && isFinite(minArea) && minArea >= 0) {
      params.minArea = minArea;
    }
    if (customFilters.maxArea !== "" && isFinite(maxArea) && maxArea >= 0) {
      params.maxArea = maxArea;
    }

    if (customFilters.furnished !== null && customFilters.furnished !== undefined) {
      params.furnished = customFilters.furnished;
    }

    if (Array.isArray(customFilters.amenities) && customFilters.amenities.length) {

      const safeAmenities = customFilters.amenities.filter((a) =>
        ALLOWED_AMENITIES.includes(a)
      );

      if (safeAmenities.length) {

        // IMPORTANT FIX
        params.amenities = safeAmenities.join(",");

      }
    }

    const maxAge = Number(customFilters.maxAge);
    if (customFilters.maxAge !== "" && isFinite(maxAge) && maxAge >= 0) {
      params.maxAge = maxAge;
    }

    const floor = Number(customFilters.floor);
    if (customFilters.floor !== "" && isFinite(floor) && floor >= 0) {
      params.floor = floor;
    }

    if (customFilters.negotiable !== null && customFilters.negotiable !== undefined) {
      params.negotiable = customFilters.negotiable;
    }

    if (customFilters.sortBy && ALLOWED_SORT_BY.includes(customFilters.sortBy)) {
      params.sortBy = customFilters.sortBy;
    }

    return params;
  }, []);

  const fetchSpaces = useCallback(
    async (customFilters = filters, page = 1, pageSize = 15) => {
      try {
        setLoading(true);
        const params = buildParams(customFilters, page, pageSize);
        const res = await API.get("/commercial-spaces", { params });
        setSpaces(res.data.listings || []);
        setPagination({
          current: res.data.currentPage || 1,
          pageSize,
          total: res.data.total || 0,
        });
      } catch (err) {
        console.error("Fetch error:", err);
        setSpaces([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchSpaces(filters, 1, 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Run per-field validation, collect errors, scroll to first ────────────
  const runFieldValidations = (filtersToValidate, customPropType) => {
    const errors = {};

    const localityResult = validateLocality(filtersToValidate.locality);
    if (!localityResult.valid) errors.locality = localityResult.message;

    const priceResult = validateNumericRange(
      filtersToValidate.minPrice,
      filtersToValidate.maxPrice,
      "Price"
    );
    if (!priceResult.valid) errors.price = priceResult.message;

    const areaResult = validateNumericRange(
      filtersToValidate.minArea,
      filtersToValidate.maxArea,
      "Area"
    );
    if (!areaResult.valid) errors.area = areaResult.message;

    const ageResult = validatePositiveInteger(filtersToValidate.maxAge, "Max Property Age");
    if (!ageResult.valid) errors.maxAge = ageResult.message;

    const floorResult = validatePositiveInteger(filtersToValidate.floor, "Floor");
    if (!floorResult.valid) errors.floor = floorResult.message;

    if (filtersToValidate.propertyType === "other") {
      const customResult = validateCustomText(customPropType, "Custom Property Type");
      if (!customResult.valid) errors.customPropertyType = customResult.message;
    }

    setFieldErrors(errors);

    // Scroll to first error (DOM order)
    const refMap = {
      locality: localityRef,
      price: priceRef,
      area: areaRef,
      maxAge: maxAgeRef,
      floor: floorRef,
      customPropertyType: customPropertyTypeRef,
    };
    scrollToFirstError(refMap, errors);

    return Object.keys(errors).length === 0;
  };

  // ── REAL‑TIME ERROR CLEARING ─────────────────────────────────────────────
  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return;

    const newErrors = { ...fieldErrors };

    // Locality
    if (newErrors.locality) {
      const localityResult = validateLocality(filters.locality);
      if (localityResult.valid) delete newErrors.locality;
    }

    // Price
    if (newErrors.price) {
      const priceResult = validateNumericRange(filters.minPrice, filters.maxPrice, "Price");
      if (priceResult.valid) delete newErrors.price;
    }

    // Area
    if (newErrors.area) {
      const areaResult = validateNumericRange(filters.minArea, filters.maxArea, "Area");
      if (areaResult.valid) delete newErrors.area;
    }

    // Max Age
    if (newErrors.maxAge) {
      const ageResult = validatePositiveInteger(filters.maxAge, "Max Property Age");
      if (ageResult.valid) delete newErrors.maxAge;
    }

    // Floor
    if (newErrors.floor) {
      const floorResult = validatePositiveInteger(filters.floor, "Floor");
      if (floorResult.valid) delete newErrors.floor;
    }

    // Custom Property Type
    if (newErrors.customPropertyType) {
      const customResult = validateCustomText(customPropertyTypeFilter, "Custom Property Type");
      if (customResult.valid) delete newErrors.customPropertyType;
    }

    if (Object.keys(newErrors).length !== Object.keys(fieldErrors).length) {
      setFieldErrors(newErrors);
    }
  }, [
    filters.locality,
    filters.minPrice,
    filters.maxPrice,
    filters.minArea,
    filters.maxArea,
    filters.maxAge,
    filters.floor,
    customPropertyTypeFilter,
    fieldErrors,
    setFieldErrors,
  ]);

  useEffect(() => {
    const handleOutsideClick = (e) => {

      // SEARCH DROPDOWN
      const clickedSearchArea = e.target.closest(
        ".sp-search-field"
      );

      if (!clickedSearchArea) {
        setSearchSuggestions([]);
      }

      // FILTER LOCALITY DROPDOWN
      const clickedFilterArea = e.target.closest(
        ".sp-filter-group"
      );

      if (!clickedFilterArea) {
        setFilterSuggestions([]);
      }

      // CUSTOM SELECT DROPDOWNS
      const clickedCustomSelect = e.target.closest(
        ".sp-custom-select"
      );

      if (!clickedCustomSelect) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  useEffect(() => {
    if (filterModalVisible) {
      setTimeout(() => {
        const modalBody = document.querySelector(
          ".sp-filter-modal .ant-modal-body"
        );

        modalBody?.scrollTo({
          top: 0,
          behavior: "auto",
        });
      }, 0);
    }
  }, [filterModalVisible]);

  // ── handleApplyFilters (filter modal) ────────────────────────────────────
  const handleApplyFilters = async () => {
    const isValid = runFieldValidations(filters, customPropertyTypeFilter);
    if (!isValid) return;

    let updatedFilters = {
      ...filters,
      search: "",
      locality: filters.locality?.trim() || "",
    };

    if (updatedFilters.propertyType === "other") {
      const trimmedCustom = customPropertyTypeFilter.trim();
      updatedFilters.propertyType = trimmedCustom || "";
    }

    if (!updatedFilters.locality) {
      updatedFilters.latitude = null;
      updatedFilters.longitude = null;
    }

    setFilters(updatedFilters);
    setSearchSuggestions([]);
    clearAllFieldErrors();
    await fetchSpaces(updatedFilters, 1, pagination.pageSize);
    setFilterModalVisible(false);
  };

  // ── handleSearchBarApply (main search bar) ────────────────────────────────
  // Called ONLY when the Search button is clicked or Enter pressed (valid).
  const handleSearchBarApply = async () => {

    const selectedValue = searchInputValue.trim();

    if (!selectedValue) {
      handleReset();
      return;
    }

    const updatedFilters = {
      search: isSearchLocationValid ? "" : selectedValue,
      locality: isSearchLocationValid ? selectedValue : "",
      latitude: null,
      longitude: null,
      type: "",
      propertyType: "",
      minPrice: "",
      maxPrice: "",
      minArea: "",
      maxArea: "",
      furnished: null,
      amenities: [],
      minAge: "",
      maxAge: "",
      floor: "",
      negotiable: null,
      sortBy: "",
    };

    setFilters(updatedFilters);
    setSearchSuggestions([]);
    clearAllFieldErrors();
    await fetchSpaces(updatedFilters, 1, pagination.pageSize);
  };

  const handleReset = () => {
    const resetFilters = {
      search: "",
      locality: "",
      latitude: null,
      longitude: null,
      type: "",
      propertyType: "",
      minPrice: "",
      maxPrice: "",
      minArea: "",
      maxArea: "",
      furnished: null,
      amenities: [],
      minAge: "",
      maxAge: "",
      floor: "",
      negotiable: null,
      sortBy: "",
    };
    setFilters(resetFilters);
    setCustomPropertyTypeFilter("");
    resetSearchLocation();
    clearAllFieldErrors();
    fetchSpaces(resetFilters, 1, pagination.pageSize);
    const modalBody = document.querySelector(
      ".sp-filter-modal .ant-modal-body"
    );

    modalBody?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handlePageChange = (page) => {
    fetchSpaces(filters, page, 15);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatPrice = (price, type) => {
    if (!price && price !== 0) return { amount: "Price on request", period: "" };
    const formatted = `₹${Number(price).toLocaleString("en-IN")}`;
    if (type === "rent") return { amount: formatted, period: "/month" };
    if (type === "lease") return { amount: formatted, period: "/year" };
    return { amount: formatted, period: "" };
  };

  const activeFiltersCount = [
    filters.locality,
    filters.type,
    filters.propertyType === "other"
      ? customPropertyTypeFilter
      : filters.propertyType,
    filters.minPrice,
    filters.maxPrice,
    filters.minArea,
    filters.maxArea,
    filters.furnished,
    filters.amenities?.length,
    filters.maxAge,
    filters.floor,
    filters.negotiable,
    filters.sortBy,
  ].filter(Boolean).length;

  const filterDrawer = (
    <div className="sp-filter-panel">
      <Space orientation="vertical" size={14} style={{ width: "100%" }}>

        {/* Locality */}
        <div className="sp-filter-group vt-field-wrap" ref={localityRef}>
          <label className="sp-filter-label">Locality</label>
          <div
            className="sp-search-custom"
            ref={filterDropdownRef}
          >
            <input
              type="text"
              className={`sp-filter-input${fieldErrors.locality ? " vt-input-error" : ""
                }`}
              placeholder="Search locality"
              maxLength={100}
              value={filters.locality}
              onChange={(e) => {
                const value = e.target.value;

                setFilters((prev) => ({
                  ...prev,
                  locality: value,
                  latitude: null,
                  longitude: null,
                }));

                clearTimeout(filterDebounceRef.current);

                if (!value || value.length < 2) {
                  setFilterSuggestions([]);
                  return;
                }

                filterDebounceRef.current = setTimeout(async () => {
                  try {
                    const results = await filterCities(value);
                    setFilterSuggestions(results || []);
                  } catch (err) {
                    console.error(err);
                  }
                }, 500);
              }}
            />

            {filterSuggestions.length > 0 && (
              <div className="sp-filter-dropdown-custom">
                {filterSuggestions.map((item, index) => (
                  <div
                    key={index}
                    className="sp-filter-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        locality: item.value,
                        latitude: null,
                        longitude: null,
                      }));

                      setFilterSuggestions([]);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          <ValidationTooltip message={fieldErrors.locality} />
        </div>

        {/* Pricing Type */}
        <div className="sp-filter-group">
          <label className="sp-filter-label">Pricing Type</label>
          <div className="sp-custom-select">
            <div
              className="sp-custom-select-trigger"
              onClick={() =>
                setOpenDropdown(
                  openDropdown === "pricingType"
                    ? null
                    : "pricingType"
                )
              }
            >
              {filters.type
                ? filters.type.charAt(0).toUpperCase() +
                filters.type.slice(1)
                : "Select type"}

              <span className="sp-custom-arrow">⌄</span>
            </div>

            {openDropdown === "pricingType" && (
              <div className="sp-custom-dropdown">
                {[
                  { label: "Rent", value: "rent" },
                  { label: "Lease", value: "lease" },
                  { label: "Sale", value: "sell" },
                ].map((item) => (
                  <div
                    key={item.value}
                    className="sp-custom-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        type: item.value,
                      }));

                      setOpenDropdown(null);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Property Type */}
        <div className="sp-filter-group">
          <label className="sp-filter-label">Property Type</label>
          <div className="sp-custom-select">
            <div
              className="sp-custom-select-trigger"
              onClick={() =>
                setOpenDropdown(
                  openDropdown === "propertyType"
                    ? null
                    : "propertyType"
                )
              }
            >
              {filters.propertyType
                ? filters.propertyType.charAt(0).toUpperCase() +
                filters.propertyType.slice(1)
                : "Select property"}

              <span className="sp-custom-arrow">⌄</span>
            </div>

            {openDropdown === "propertyType" && (
              <div className="sp-custom-dropdown">
                {[
                  "office",
                  "shop",
                  "warehouse",
                  "showroom",
                  "coworking",
                  "other",
                ].map((item) => (
                  <div
                    key={item}
                    className="sp-custom-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        propertyType: item,
                      }));

                      if (item !== "other") {
                        setCustomPropertyTypeFilter("");
                      }

                      setOpenDropdown(null);
                    }}
                  >
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Custom property type input */}
        {filters.propertyType === "other" && (
          <div
            className="sp-filter-group vt-field-wrap"
            style={{ marginTop: 8 }}
            ref={customPropertyTypeRef}
          >
            <label className="sp-filter-label">Custom Property Type</label>
            <input
              type="text"
              placeholder="e.g. Restaurant, Clinic, Bank"
              value={customPropertyTypeFilter}
              maxLength={100}
              onChange={(e) => setCustomPropertyTypeFilter(e.target.value)}
              className={`sp-filter-input${fieldErrors.customPropertyType ? " vt-input-error" : ""}`}
            />
            <ValidationTooltip message={fieldErrors.customPropertyType} />
          </div>
        )}

        {/* Price Range */}
        <div className="sp-filter-group vt-field-wrap" ref={priceRef}>
          <label className="sp-filter-label">Price Range (₹)</label>
          <div className="sp-filter-row">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Min"
              min={0}
              max={MAX_PRICE}
              value={filters.minPrice}
              onChange={(e) => {
                const val = sanitizeNumericInput(e.target.value, MAX_PRICE);
                setFilters((p) => ({ ...p, minPrice: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className={`sp-filter-input-half${fieldErrors.price ? " vt-input-error" : ""}`}
            />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Max"
              min={0}
              max={MAX_PRICE}
              value={filters.maxPrice}
              onChange={(e) => {
                const val = sanitizeNumericInput(e.target.value, MAX_PRICE);
                setFilters((p) => ({ ...p, maxPrice: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className={`sp-filter-input-half${fieldErrors.price ? " vt-input-error" : ""}`}
            />
          </div>
          <ValidationTooltip message={fieldErrors.price} />
        </div>

        {/* Area */}
        <div className="sp-filter-group vt-field-wrap" ref={areaRef}>
          <label className="sp-filter-label">Area (sqft)</label>
          <div className="sp-filter-row">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Min"
              min={0}
              max={MAX_AREA}
              value={filters.minArea}
              onChange={(e) => {
                const val = sanitizeNumericInput(e.target.value, MAX_AREA);
                setFilters((p) => ({ ...p, minArea: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className={`sp-filter-input-half${fieldErrors.area ? " vt-input-error" : ""}`}
            />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Max"
              min={0}
              max={MAX_AREA}
              value={filters.maxArea}
              onChange={(e) => {
                const val = sanitizeNumericInput(e.target.value, MAX_AREA);
                setFilters((p) => ({ ...p, maxArea: val }));
              }}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              className={`sp-filter-input-half${fieldErrors.area ? " vt-input-error" : ""}`}
            />
          </div>
          <ValidationTooltip message={fieldErrors.area} />
        </div>

        {/* Furnished */}
        <div className="sp-filter-group">
          <label className="sp-filter-label">Furnished</label>
          <div className="sp-custom-select">
            <div
              className="sp-custom-select-trigger"
              onClick={() =>
                setOpenDropdown(
                  openDropdown === "furnished"
                    ? null
                    : "furnished"
                )
              }
            >
              {filters.furnished === null
                ? "Select furnished"
                : filters.furnished
                  ? "Furnished"
                  : "Unfurnished"}

              <span className="sp-custom-arrow">⌄</span>
            </div>

            {openDropdown === "furnished" && (
              <div className="sp-custom-dropdown">
                {[
                  { label: "Furnished", value: true },
                  { label: "Unfurnished", value: false },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="sp-custom-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        furnished: item.value,
                      }));

                      setOpenDropdown(null);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Amenities */}
        <div className="sp-filter-group">
          <label className="sp-filter-label">Amenities</label>
          <div className="sp-custom-select">
            <div
              className="sp-custom-select-trigger"
              onClick={() =>
                setOpenDropdown(
                  openDropdown === "amenities"
                    ? null
                    : "amenities"
                )
              }
            >
              {filters.amenities.length > 0
                ? filters.amenities.join(", ")
                : "Select amenities"}

              <span className="sp-custom-arrow">⌄</span>
            </div>

            {openDropdown === "amenities" && (
              <div className="sp-custom-dropdown">
                {ALLOWED_AMENITIES.map((item) => {
                  const isSelected =
                    filters.amenities.includes(item);

                  return (
                    <div
                      key={item}
                      className={`sp-custom-dropdown-item ${isSelected
                        ? "sp-custom-dropdown-item-active"
                        : ""
                        }`}
                      onClick={() => {
                        setFilters((prev) => ({
                          ...prev,
                          amenities: isSelected
                            ? prev.amenities.filter(
                              (a) => a !== item
                            )
                            : [...prev.amenities, item],
                        }));
                      }}
                    >
                      <span>{item}</span>

                      {isSelected && (
                        <span className="sp-checkmark">
                          ✓
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Max Property Age */}
        <div className="sp-filter-group vt-field-wrap" ref={maxAgeRef}>
          <label className="sp-filter-label">Max Property Age (years)</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            min={0}
            max={MAX_AGE}
            value={filters.maxAge}
            onChange={(e) => {
              const val = sanitizeNumericInput(e.target.value, MAX_AGE);
              setFilters((p) => ({ ...p, maxAge: val }));
            }}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-", "."].includes(e.key)) {
                e.preventDefault();
              }
            }}
            className={`sp-filter-input${fieldErrors.maxAge ? " vt-input-error" : ""}`}
          />
          <ValidationTooltip message={fieldErrors.maxAge} />
        </div>

        {/* Floor */}
        <div className="sp-filter-group vt-field-wrap" ref={floorRef}>
          <label className="sp-filter-label">Floor</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            min={0}
            max={MAX_FLOOR}
            value={filters.floor}
            onChange={(e) => {
              const val = sanitizeNumericInput(e.target.value, MAX_FLOOR);
              setFilters((p) => ({ ...p, floor: val }));
            }}
            onKeyDown={(e) => {
              if (["e", "E", "+", "-", "."].includes(e.key)) {
                e.preventDefault();
              }
            }}
            className={`sp-filter-input${fieldErrors.floor ? " vt-input-error" : ""}`}
          />
          <ValidationTooltip message={fieldErrors.floor} />
        </div>

        {/* Negotiable */}
        <div className="sp-filter-group">
          <label className="sp-filter-label">Negotiable</label>
          <div className="sp-custom-select">
            <div
              className="sp-custom-select-trigger"
              onClick={() =>
                setOpenDropdown(
                  openDropdown === "negotiable"
                    ? null
                    : "negotiable"
                )
              }
            >
              {filters.negotiable === null
                ? "Select negotiable"
                : filters.negotiable
                  ? "Yes"
                  : "No"}

              <span className="sp-custom-arrow">⌄</span>
            </div>

            {openDropdown === "negotiable" && (
              <div className="sp-custom-dropdown">
                {[
                  { label: "Yes", value: true },
                  { label: "No", value: false },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="sp-custom-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        negotiable: item.value,
                      }));

                      setOpenDropdown(null);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sort By */}
        <div className="sp-filter-group">
          <label className="sp-filter-label">Sort By</label>
          <div className="sp-custom-select">
            <div
              className="sp-custom-select-trigger"
              onClick={() =>
                setOpenDropdown(
                  openDropdown === "sortBy"
                    ? null
                    : "sortBy"
                )
              }
            >
              {filters.sortBy
                ? filters.sortBy
                  .replace("_", " ")
                  .replace("_", " ")
                : "Sort By"}

              <span className="sp-custom-arrow">⌄</span>
            </div>

            {openDropdown === "sortBy" && (
              <div className="sp-custom-dropdown">
                {[
                  {
                    label: "Price Low → High",
                    value: "price_low",
                  },
                  {
                    label: "Price High → Low",
                    value: "price_high",
                  },
                  {
                    label: "Area High → Low",
                    value: "area_high",
                  },
                ].map((item) => (
                  <div
                    key={item.value}
                    className="sp-custom-dropdown-item"
                    onClick={() => {
                      setFilters((prev) => ({
                        ...prev,
                        sortBy: item.value,
                      }));

                      setOpenDropdown(null);
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="sp-filter-actions">
          <Button block onClick={handleApplyFilters} className="sp-apply-btn">
            Apply Filters
          </Button>
          <Button
            block
            icon={<ReloadOutlined />}
            onClick={handleReset}
            className="sp-reset-btn"
          >
            Reset All
          </Button>
        </div>
      </Space>
    </div>
  );

  return (
    <div className="sp-page">
      <div className="sp-container">

        {/* Hero */}
        <header className="sp-hero">
          <div className="sp-hero-content">
            <span className="sp-hero-tag">Real Estate Marketplace</span>
            <h1 className="sp-hero-title">Commercial Spaces</h1>
            <p className="sp-hero-desc">
              Discover premium offices, retail outlets, and industrial spaces for your business
            </p>
          </div>
          <div className="sp-hero-decoration" aria-hidden="true">
            <div className="sp-hero-circle sp-hero-circle--lg" />
            <div className="sp-hero-circle sp-hero-circle--sm" />
          </div>
        </header>

        {/* Search & Filter Bar */}
        <section className="sp-bar">
          <div className="sp-bar-top">
            <div className="sp-bar-count">
              <strong>{pagination.total.toLocaleString()}</strong> properties available
            </div>
            {activeFiltersCount > 0 && (
              <button className="sp-clear-all" onClick={handleReset}>
                Clear all ({activeFiltersCount})
              </button>
            )}
          </div>

          <div className="sp-bar-controls">
            {/* Search */}
            <div className="sp-search-field">
              <SearchOutlined className="sp-search-ico" />
              <div
                className="sp-search-custom"
                ref={searchDropdownRef}
              >
                <input
                  type="text"
                  className="sp-search-input"
                  placeholder="Search by location, listing name, or property type..."
                  maxLength={200}
                  value={searchInputValue}
                  onFocus={handleSearchFocus}
                  onChange={(e) => {
                    const value = e.target.value;

                    handleSearchChange(value);

                    setFilters((prev) => ({
                      ...prev,
                      search: value.slice(0, 200),
                      locality: "",
                    }));

                    const looksLikeLocation =
                      /nagar|layout|city|phase|road|street|colony/i.test(value);

                    if (!looksLikeLocation) {
                      setSearchSuggestions([]);
                    }
                  }}
                  onKeyDown={makeSearchKeyDown(handleSearchBarApply)}
                />

                {searchSuggestions.length > 0 && (
                  <div className="sp-search-dropdown-custom">
                    {searchSuggestions.map((item, index) => (
                      <div
                        key={index}
                        className="sp-search-dropdown-item"
                        onClick={() => {
                          handleSearchSelect(item.value);
                          setSearchSuggestions([]);
                        }}
                      >
                        {item.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/*
             * UPGRADED Search button
             *
             * - disabled when no valid dropdown location has been selected
             */}
            <button
              className="sp-search-btn"
              onClick={handleSearchBarApply}
              disabled={loading}
            >
              Search
            </button>

            <button
              className="sp-filter-btn"
              onClick={() => setFilterModalVisible(true)}
            >
              <FilterOutlined />
              <span className="sp-filter-btn-label">Filters</span>
              {activeFiltersCount > 0 && (
                <span className="sp-filter-badge">{activeFiltersCount}</span>
              )}
            </button>
          </div>
        </section>

        {/* Modal Filter Panel */}
        <Modal
          title="Filter Properties"
          open={filterModalVisible}
          onCancel={() => {
            setFilterModalVisible(false);
            clearAllFieldErrors();
          }}
          footer={null}
          centered
          width={modalWidth}
          className="sp-filter-modal"
          mask={{ closable: true }}
          styles={{
            body: {
              overflowY: "auto",
            },
          }}
        >
          {filterDrawer}
        </Modal>

        {/* Grid / States */}
        {loading ? (
          <div className="sp-state-wrap">
            <Spin size="large" />
            <p className="sp-state-msg">Loading properties…</p>
          </div>
        ) : spaces.length === 0 ? (
          <div className="sp-state-wrap">
            <Empty description={<span className="sp-state-msg">No properties found</span>} />
          </div>
        ) : (
          <>
            <Row gutter={[20, 20]} className="sp-grid">
              {spaces.map((space) => {
                const price = formatPrice(space.pricing?.price, space.pricing?.type);
                const pricingType = space.pricing?.type;
                const listingLabel =
                  pricingType === "rent"
                    ? "For Rent"
                    : pricingType === "lease"
                      ? "For Lease"
                      : "For Sale";

                return (
                  <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={6} key={space._id}>
                    <article className="sp-card">
                      <div className="sp-card-thumb">
                        <img
                          alt={space.title}
                          src={
                            space.thumbnail ||
                            "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80"
                          }
                          className="sp-card-img"
                          loading="lazy"
                        />
                        <div className="sp-card-overlay" />
                        <div className="sp-card-tags">
                          <span
                            className={`sp-tag sp-tag--listing sp-tag--${pricingType || "sell"}`}
                          >
                            {listingLabel}
                          </span>
                          <span className="sp-tag sp-tag--type">
                            {space.propertyDetails?.propertyType || "Property"}
                          </span>
                        </div>
                      </div>
                      <div className="sp-card-body">
                        <div className="sp-card-location">
                          <EnvironmentOutlined className="sp-loc-ico" />
                          <span className="sp-loc-text">
                            {[
                              space.location?.locality,
                              space.location?.city,
                              space.location?.state,
                            ]
                              .filter(Boolean)
                              .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
                              .join(", ")}
                          </span>
                        </div>
                        <h3 className="sp-card-name">{space.title}</h3>
                        <div className="sp-card-price">
                          <span className="sp-price-num">{price.amount}</span>
                          {price.period && (
                            <span className="sp-price-unit">{price.period}</span>
                          )}
                        </div>
                        <div className="sp-card-specs">
                          <div className="sp-spec-item">
                            <span className="sp-spec-ico">▪</span>
                            <span>{space.propertyDetails?.areaSqFt || 0} sqft</span>
                          </div>
                          {space.propertyDetails?.bedrooms ? (
                            <div className="sp-spec-item">
                              <span className="sp-spec-ico">⊞</span>
                              <span>{space.propertyDetails.bedrooms} Bed</span>
                            </div>
                          ) : null}
                          {space.propertyDetails?.bathrooms ? (
                            <div className="sp-spec-item">
                              <span className="sp-spec-ico">◉</span>
                              <span>{space.propertyDetails.bathrooms} Bath</span>
                            </div>
                          ) : null}
                        </div>
                        <div className="sp-card-footer">
                          <Link
                            to={`/commercial-space/${space.slug}`}
                            className="sp-card-cta"
                          >
                            View details →
                          </Link>
                        </div>
                      </div>
                    </article>
                  </Col>
                );
              })}
            </Row>

            <div className="sp-pagination">
              <Pagination
                current={pagination.current}
                pageSize={15}
                total={pagination.total}
                onChange={handlePageChange}
                responsive
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CommercialSpaces;